import { __ } from '@wordpress/i18n';

// Define interfaces
export interface Field {
    key: string;
    label: string | (() => string);
    help?: string;
    group: string;
    fields: SubField[];
    hasSimplifiedLayout?: boolean;
    hasMultipleSubFields?: boolean;
}

export interface SubField {
    type: 'subField' | 'operatorField' | 'valueField';
    name?: string;
    valueType?: string;
    options?: Array<{ label: string; value: string }>;
    placeholder?: string;
    displayConditions?: Array<DisplayCondition>;
    conditionalValueTypes?: Array<ConditionalValueType>;
    valueTypeVariant?: string;
    triggerReset?: boolean;
    help?: string;
}

export interface DisplayCondition {
    dependencyType: 'subField' | 'operatorField' | 'valueField';
    dependencyName?: string;
    dependencyValues: string[];
}

export interface ConditionalValueType {
    dependencyType: 'subField' | 'operatorField' | 'valueField';
    dependencyName?: string;
    dependencyValues: string[];
    valueTypes: Array<{
        value: string;
        valueType: string;
        placeholder?: string;
        valueTypeVariant?: string;
    }>;
}

// Abstracted operator options
const VALUE_OPERATORS = [
    { value: 'equal', label: __( 'Is equal to', 'checkout-wc' ) },
    { value: 'notEqual', label: __( 'Is not equal to', 'checkout-wc' ) },
    { value: 'greaterThan', label: __( 'Is greater than', 'checkout-wc' ) },
    { value: 'lessThan', label: __( 'Is less than', 'checkout-wc' ) },
    { value: 'greaterThanEqual', label: __( 'Is greater or equal to', 'checkout-wc' ) },
    { value: 'lessThanEqual', label: __( 'Is less than or equal to', 'checkout-wc' ) },
];

const DATE_VALUE_OPERATORS = [
    { value: 'equal', label: __( 'Was placed on', 'checkout-wc' ) },
    { value: 'notEqual', label: __( 'Was not placed on', 'checkout-wc' ) },
    { value: 'greaterThan', label: __( 'Was placed after', 'checkout-wc' ) },
    { value: 'lessThan', label: __( 'Was placed before', 'checkout-wc' ) },
];

const DATE_VALUE_SIMPLIFIED_OPERATORS = [
    { value: 'equal', label: __( 'On', 'checkout-wc' ) },
    { value: 'notEqual', label: __( 'Not on', 'checkout-wc' ) },
    { value: 'greaterThan', label: __( 'After', 'checkout-wc' ) },
    { value: 'lessThan', label: __( 'Before', 'checkout-wc' ) },
];

const CONTAINS_OPERATORS = [
    { value: 'atLeastOne', label: __( 'At least one of the selected', 'checkout-wc' ) },
    { value: 'all', label: __( 'All of the selected', 'checkout-wc' ) },
    { value: 'none', label: __( 'None of the selected', 'checkout-wc' ) },
];

// Placeholders
const OPERATOR_PLACEHOLDER = __( 'Select Condition…', 'checkout-wc' );
const ORDER_TYPE_PLACEHOLDER = __( 'Select Order Type…', 'checkout-wc' );
const SELECT_CATEGORY_PLACEHOLDER = __( 'Select Product Category…', 'checkout-wc' );
const SELECT_TAG_PLACEHOLDER = __( 'Select Product Tag…', 'checkout-wc' );
const SELECT_PRODUCT_PLACEHOLDER = __( 'Select Product…', 'checkout-wc' );
const SELECT_INVENTORY_STATUS_PLACEHOLDER = __( 'Select Inventory Status…', 'checkout-wc' );
const SELECT_USER_ROLE_PLACEHOLDER = __( 'Select User Role…', 'checkout-wc' );

export const fields: Field[] = [
    // Cart fields
    {
        key: 'cartContents',
        label: __( 'Cart Contents', 'checkout-wc' ),
        group: 'cart',
        fields: [
            {
                type: 'subField',
                name: 'cartContents',
                valueType: 'select',
                options: [
                    { value: 'empty', label: __( 'Is empty', 'checkout-wc' ) },
                    { value: 'notEmpty', label: __( 'Is not empty', 'checkout-wc' ) },
                    { value: 'containsProducts', label: __( 'Contains (Products)', 'checkout-wc' ) },
                    { value: 'containsCategories', label: __( 'Contains (Categories)', 'checkout-wc' ) },
                    { value: 'containsTags', label: __( 'Contains (Tags)', 'checkout-wc' ) },
                    { value: 'containsAttributes', label: __( 'Contains (Global Product Attributes)', 'checkout-wc' ) },
                ],
                placeholder: OPERATOR_PLACEHOLDER,
                triggerReset: true,
            },
            {
                type: 'operatorField',
                valueType: 'select',
                options: CONTAINS_OPERATORS,
                placeholder: OPERATOR_PLACEHOLDER,
                displayConditions: [
                    {
                        dependencyType: 'subField',
                        dependencyName: 'cartContents',
                        dependencyValues: [ 'containsProducts', 'containsCategories', 'containsTags', 'containsAttributes' ],
                    },
                ],
            },
            {
                type: 'valueField',
                conditionalValueTypes: [
                    {
                        dependencyType: 'subField',
                        dependencyName: 'cartContents',
                        dependencyValues: [ 'containsProducts', 'containsCategories', 'containsTags', 'containsAttributes' ],
                        valueTypes: [
                            {
                                value: 'containsProducts',
                                valueType: 'productsSelect',
                                placeholder: SELECT_PRODUCT_PLACEHOLDER,
                            },
                            {
                                value: 'containsCategories',
                                valueType: 'termsSelect',
                                valueTypeVariant: 'product_cat',
                                placeholder: SELECT_CATEGORY_PLACEHOLDER,
                            },
                            {
                                value: 'containsTags',
                                valueType: 'termsSelect',
                                valueTypeVariant: 'product_tag',
                                placeholder: SELECT_TAG_PLACEHOLDER,
                            },
                            {
                                value: 'containsAttributes',
                                valueType: 'termsSelect',
                                valueTypeVariant: 'product_attributes',
                                placeholder: __( 'Select Attribute Terms...', 'checkout-wc' ),
                            },
                        ],
                    },
                ],
                displayConditions: [
                    {
                        dependencyType: 'subField',
                        dependencyName: 'cartContents',
                        dependencyValues: [ 'containsProducts', 'containsCategories', 'containsTags', 'containsAttributes' ],
                    },
                ],
            },
        ],
    },
    {
        key: 'cartTotalQuantity',
        label: __( 'Cart Total Quantity', 'checkout-wc' ),
        group: 'cart',
        hasSimplifiedLayout: true,
        fields: [
            {
                type: 'operatorField',
                valueType: 'select',
                options: VALUE_OPERATORS,
                placeholder: OPERATOR_PLACEHOLDER,
            },
            {
                type: 'valueField',
                valueType: 'number',
            },
        ],
    },
    {
        key: 'cartTotalValue',
        label: __( 'Cart Subtotal', 'checkout-wc' ),
        group: 'cart',
        hasSimplifiedLayout: true,
        fields: [
            {
                type: 'operatorField',
                valueType: 'select',
                options: VALUE_OPERATORS,
                placeholder: OPERATOR_PLACEHOLDER,
            },
            {
                type: 'valueField',
                valueType: 'number',
            },
        ],
    },
    {
        key: 'cartProductQuantity',
        label: __( 'Quantity of Product in Cart', 'checkout-wc' ),
        help: 'Quantity applies to each selected product.',
        group: 'cart',
        hasSimplifiedLayout: true,
        fields: [
            {
                type: 'subField',
                name: 'products',
                valueType: 'productsSelect',
                placeholder: SELECT_PRODUCT_PLACEHOLDER,
            },
            {
                type: 'operatorField',
                valueType: 'select',
                options: VALUE_OPERATORS,
                placeholder: OPERATOR_PLACEHOLDER,
            },
            {
                type: 'valueField',
                valueType: 'number',
            },
        ],
    },
    {
        key: 'cartCategoryQuantity',
        label: __( 'Quantity of Category in Cart', 'checkout-wc' ),
        help: 'Quantity of products in cart which belong to the selected category. Quantity applies to each category.',
        group: 'cart',
        hasSimplifiedLayout: true,
        fields: [
            {
                type: 'subField',
                name: 'categories',
                valueType: 'termsSelect',
                valueTypeVariant: 'product_cat',
                placeholder: SELECT_CATEGORY_PLACEHOLDER,
            },
            {
                type: 'operatorField',
                valueType: 'select',
                options: VALUE_OPERATORS,
                placeholder: OPERATOR_PLACEHOLDER,
            },
            {
                type: 'valueField',
                valueType: 'number',
            },
        ],
    },
    {
        key: 'cartCoupons',
        label: __( 'Cart Coupons', 'checkout-wc' ),
        group: 'cart',
        fields: [
            {
                type: 'subField',
                name: 'hasCoupon',
                valueType: 'select',
                options: [
                    { value: 'noCoupon', label: __( 'Has no coupon', 'checkout-wc' ) },
                    { value: 'hasCoupon', label: __( 'Has coupon', 'checkout-wc' ) },
                ],
                placeholder: OPERATOR_PLACEHOLDER,
                triggerReset: true,
            },
            {
                type: 'valueField',
                name: 'coupons',
                valueType: 'text',
                help: 'A comma separated list of coupon codes.',
                placeholder: __( 'Enter coupon codes...', 'checkout-wc' ),
                displayConditions: [
                    {
                        dependencyType: 'subField',
                        dependencyName: 'hasCoupon',
                        dependencyValues: [ 'hasCoupon' ],
                    },
                ],
            },
        ],
    },
    // Customer fields - Value
    {
        key: 'customerTotalSpent',
        label: __( 'Customer History: Total Spent', 'checkout-wc' ),
        group: 'customer-value',
        hasSimplifiedLayout: true,
        fields: [
            {
                type: 'operatorField',
                valueType: 'select',
                options: VALUE_OPERATORS,
                placeholder: OPERATOR_PLACEHOLDER,
            },
            {
                type: 'valueField',
                valueType: 'number',
            },
        ],
    },
    {
        key: 'customerAverageOrderValue',
        label: __( 'Customer History: Average Order Value', 'checkout-wc' ),
        group: 'customer-value',
        hasSimplifiedLayout: true,
        fields: [
            {
                type: 'operatorField',
                valueType: 'select',
                options: VALUE_OPERATORS,
                placeholder: OPERATOR_PLACEHOLDER,
            },
            {
                type: 'valueField',
                valueType: 'number',
            },
        ],
    },
    // Customer fields - Quantity
    {
        key: 'customerTotalOrders',
        label: __( 'Customer History: Total Orders', 'checkout-wc' ),
        group: 'customer-quantity',
        hasSimplifiedLayout: true,
        fields: [
            {
                type: 'operatorField',
                valueType: 'select',
                options: VALUE_OPERATORS,
                placeholder: OPERATOR_PLACEHOLDER,
            },
            {
                type: 'valueField',
                valueType: 'number',
            },
        ],
    },
    {
        key: 'customerQuantityProductOrdered',
        label: __( 'Customer History: Quantity of Product Ordered', 'checkout-wc' ),
        help: 'Quantity applies to each selected product.',
        group: 'customer-quantity',
        hasSimplifiedLayout: true,
        fields: [
            {
                type: 'subField',
                name: 'products',
                valueType: 'productsSelect',
                placeholder: SELECT_PRODUCT_PLACEHOLDER,
            },
            {
                type: 'operatorField',
                valueType: 'select',
                options: VALUE_OPERATORS,
                placeholder: OPERATOR_PLACEHOLDER,
            },
            {
                type: 'valueField',
                valueType: 'number',
            },
        ],
    },
    {
        key: 'customerQuantityCategoryOrdered',
        label: __( 'Customer History: Quantity of Category Ordered', 'checkout-wc' ),
        help: 'Quantity of products ordered which belong to the category. Applies to each selected category.',
        group: 'customer-quantity',
        hasSimplifiedLayout: true,
        fields: [
            {
                type: 'subField',
                name: 'categories',
                valueType: 'termsSelect',
                valueTypeVariant: 'product_cat',
                placeholder: SELECT_CATEGORY_PLACEHOLDER,
            },
            {
                type: 'operatorField',
                valueType: 'select',
                options: VALUE_OPERATORS,
                placeholder: OPERATOR_PLACEHOLDER,
            },
            {
                type: 'valueField',
                valueType: 'number',
            },
        ],
    },
    // Customer fields - Time
    {
        key: 'customerTimeSinceOrder',
        label: __( 'Customer History: Days Since Order', 'checkout-wc' ),
        help: "The number of days since the customer's order was placed.",
        group: 'customer-time',
        hasSimplifiedLayout: true,
        fields: [
            {
                type: 'subField',
                name: 'orderType',
                valueType: 'select',
                options: [
                    { value: 'first', label: __( 'First order', 'checkout-wc' ) },
                    { value: 'last', label: __( 'Last order', 'checkout-wc' ) },
                ],
                placeholder: ORDER_TYPE_PLACEHOLDER,
            },
            {
                type: 'operatorField',
                valueType: 'select',
                options: VALUE_OPERATORS,
                placeholder: OPERATOR_PLACEHOLDER,
            },
            {
                type: 'valueField',
                valueType: 'number',
                placeholder: __( 'Days', 'checkout-wc' ),
            },
        ],
    },
    {
        key: 'customerTimeSinceProductOrdered',
        label: __( 'Customer History: Days Since Product Ordered', 'checkout-wc' ),
        help: 'The number of days since the product was ordered. Applies to each selected product.',
        group: 'customer-time',
        hasSimplifiedLayout: true,
        hasMultipleSubFields: true,
        fields: [
            {
                type: 'subField',
                name: 'orderType',
                valueType: 'select',
                options: [
                    { value: 'first', label: __( 'First order', 'checkout-wc' ) },
                    { value: 'last', label: __( 'Last order', 'checkout-wc' ) },
                ],
                placeholder: ORDER_TYPE_PLACEHOLDER,
            },
            {
                type: 'subField',
                name: 'products',
                valueType: 'productsSelect',
                placeholder: SELECT_PRODUCT_PLACEHOLDER,
            },
            {
                type: 'operatorField',
                valueType: 'select',
                options: VALUE_OPERATORS,
                placeholder: OPERATOR_PLACEHOLDER,
            },
            {
                type: 'valueField',
                valueType: 'number',
                placeholder: __( 'Days', 'checkout-wc' ),
            },
        ],
    },
    {
        key: 'customerTimeSinceCategoryOrdered',
        label: __( 'Customer History: Days Since Category Ordered', 'checkout-wc' ),
        help: 'The number of days since the category was ordered. Applies to each selected category.',
        group: 'customer-time',
        hasSimplifiedLayout: true,
        hasMultipleSubFields: true,
        fields: [
            {
                type: 'subField',
                name: 'orderType',
                valueType: 'select',
                options: [
                    { value: 'first', label: __( 'First order', 'checkout-wc' ) },
                    { value: 'last', label: __( 'Last order', 'checkout-wc' ) },
                ],
                placeholder: ORDER_TYPE_PLACEHOLDER,
            },
            {
                type: 'subField',
                name: 'categories',
                valueType: 'termsSelect',
                valueTypeVariant: 'product_cat',
                placeholder: SELECT_CATEGORY_PLACEHOLDER,
            },
            {
                type: 'operatorField',
                valueType: 'select',
                options: VALUE_OPERATORS,
                placeholder: OPERATOR_PLACEHOLDER,
            },
            {
                type: 'valueField',
                valueType: 'number',
                placeholder: __( 'Days', 'checkout-wc' ),
            },
        ],
    },
    {
        key: 'customerDateOfOrder',
        label: __( 'Customer History: Date of Order', 'checkout-wc' ),
        group: 'customer-time',
        fields: [
            {
                type: 'subField',
                valueType: 'select',
                options: [
                    { value: 'first', label: __( 'First order', 'checkout-wc' ) },
                    { value: 'last', label: __( 'Last order', 'checkout-wc' ) },
                ],
                placeholder: ORDER_TYPE_PLACEHOLDER,
            },
            {
                type: 'operatorField',
                valueType: 'select',
                options: DATE_VALUE_OPERATORS,
                placeholder: OPERATOR_PLACEHOLDER,
            },
            {
                type: 'valueField',
                valueType: 'date',
            },
        ],
    },
    {
        key: 'customerDateOfProductOrdered',
        label: __( 'Customer History: Date of Product Ordered', 'checkout-wc' ),
        group: 'customer-time',
        hasMultipleSubFields: true,
        fields: [
            {
                type: 'subField',
                name: 'orderType',
                valueType: 'select',
                options: [
                    { value: 'first', label: __( 'First ordered', 'checkout-wc' ) },
                    { value: 'last', label: __( 'Last ordered', 'checkout-wc' ) },
                ],
                placeholder: ORDER_TYPE_PLACEHOLDER,
            },
            {
                type: 'subField',
                name: 'products',
                valueType: 'productsSelect',
                placeholder: SELECT_PRODUCT_PLACEHOLDER,
            },
            {
                type: 'operatorField',
                valueType: 'select',
                options: DATE_VALUE_SIMPLIFIED_OPERATORS,
                placeholder: OPERATOR_PLACEHOLDER,
            },
            {
                type: 'valueField',
                valueType: 'date',
            },
        ],
    },
    {
        key: 'customerDateOfCategoryOrdered',
        label: __( 'Customer History: Date of Category Ordered', 'checkout-wc' ),
        group: 'customer-time',
        hasMultipleSubFields: true,
        fields: [
            {
                type: 'subField',
                name: 'orderType',
                valueType: 'select',
                options: [
                    { value: 'first', label: __( 'First ordered', 'checkout-wc' ) },
                    { value: 'last', label: __( 'Last ordered', 'checkout-wc' ) },
                ],
                placeholder: ORDER_TYPE_PLACEHOLDER,
            },
            {
                type: 'subField',
                name: 'categories',
                valueType: 'termsSelect',
                valueTypeVariant: 'product_cat',
                placeholder: SELECT_CATEGORY_PLACEHOLDER,
            },
            {
                type: 'operatorField',
                valueType: 'select',
                options: DATE_VALUE_SIMPLIFIED_OPERATORS,
                placeholder: OPERATOR_PLACEHOLDER,
            },
            {
                type: 'valueField',
                valueType: 'date',
            },
        ],
    },
    // Product fields
    {
        key: 'productInventory',
        label: __( 'Product Inventory', 'checkout-wc' ),
        group: 'product',
        fields: [
            {
                type: 'subField',
                name: 'products',
                valueType: 'productsSelect',
                placeholder: SELECT_PRODUCT_PLACEHOLDER,
            },
            {
                type: 'valueField',
                valueType: 'select',
                options: [
                    { value: 'inStock', label: __( 'Is in stock', 'checkout-wc' ) },
                    { value: 'outOfStock', label: __( 'Is out of stock', 'checkout-wc' ) },
                    { value: 'onBackorder', label: __( 'Is on backorder', 'checkout-wc' ) },
                ],
                placeholder: SELECT_INVENTORY_STATUS_PLACEHOLDER,
            },
        ],
    },
    {
        key: 'productQuantityInStock',
        label: __( 'Quantity of Product in Stock', 'checkout-wc' ),
        group: 'product',
        hasSimplifiedLayout: true,
        fields: [
            {
                type: 'subField',
                name: 'products',
                valueType: 'productsSelect',
                placeholder: SELECT_PRODUCT_PLACEHOLDER,
            },
            {
                type: 'operatorField',
                valueType: 'select',
                options: VALUE_OPERATORS,
                placeholder: OPERATOR_PLACEHOLDER,
            },
            {
                type: 'valueField',
                valueType: 'number',
            },
        ],
    },
    {
        key: 'shippingCountry',
        label: __( 'Shipping Country', 'checkout-wc' ),
        group: 'customer',
        hasSimplifiedLayout: true,
        fields: [
            {
                type: 'operatorField',
                valueType: 'select',
                options: CONTAINS_OPERATORS,
                placeholder: OPERATOR_PLACEHOLDER,
            },
            {
                type: 'valueField',
                valueType: 'countries',
            },
        ],
    },
    {
        key: 'billingCountry',
        label: __( 'Billing Country', 'checkout-wc' ),
        group: 'customer',
        hasSimplifiedLayout: true,
        fields: [
            {
                type: 'operatorField',
                valueType: 'select',
                options: CONTAINS_OPERATORS,
                placeholder: OPERATOR_PLACEHOLDER,
            },
            {
                type: 'valueField',
                valueType: 'countries',
            },
        ],
    },
    {
        key: 'userRole',
        // Get the plan level and determine if we should show the Pro+ label
        get label() {
            const planLevel = (window as any).cfwOrderBumpsData?.plan_level || 0;
            return planLevel >= 3 ? 'User Role' : 'User Role (Pro+)';
        },
        help: 'Check if the current user has a specific role',
        group: 'customer',
        hasSimplifiedLayout: true,
        fields: [
            {
                type: 'operatorField',
                valueType: 'select',
                options: CONTAINS_OPERATORS,
                placeholder: OPERATOR_PLACEHOLDER,
            },
            {
                type: 'valueField',
                valueType: 'userRoles',
                placeholder: SELECT_USER_ROLE_PLACEHOLDER,
            },
        ],
    },
];
